import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { clsx } from 'clsx/lite';
import { EmptyState } from 'components';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import { CSSProperties, FC, forwardRef, memo } from 'react';
import { SimsDaoSim } from 'types/__generated__/SimInventoryApi';
import { SimInventorySegmentName, SimKey } from 'types/common';
import LoaderCell from './ColumnDefinition/LoaderCell';
import styles from './SimInventoryTable.module.css';
import SimInventoryTableCheckbox from './SimInventoryTableCheckbox';
import SimInventoryTableHeaderCell from './SimInventoryTableHeaderCell';
import {
  ColumnDefs,
  CustomCellRenderer,
  MeasureElement,
  OnAllCheckboxToggled,
  OnCheckboxToggled,
  SimCellRendererProps,
} from './SimInventoryTableShared';

interface CellContentRendererProps {
  sim: SimsDaoSim;
  segmentName: SimInventorySegmentName;
  ColumnRenderer?: CustomCellRenderer<SimsDaoSim, unknown>;
  isHeader: boolean;
  customRendererProps: SimCellRendererProps;
  columnValue: SimKey | 'checkbox';
  allowToggle: boolean;
  onCheckboxToggled: OnCheckboxToggled;
  onAllCheckboxToggled: OnAllCheckboxToggled;
}

const CellContentRenderer: FC<CellContentRendererProps> = ({
  sim,
  segmentName,
  ColumnRenderer,
  isHeader,
  customRendererProps,
  columnValue,
  allowToggle,
  onCheckboxToggled,
  onAllCheckboxToggled,
}) => {
  const value = columnValue !== 'checkbox' ? sim[columnValue] : undefined;

  if (columnValue === 'checkbox') {
    return (
      <SimInventoryTableCheckbox
        isHeader={isHeader}
        sim={sim}
        segmentName={segmentName}
        allowToggle={allowToggle}
        onCheckboxToggled={onCheckboxToggled}
        onAllCheckboxToggled={onAllCheckboxToggled}
      />
    );
  }

  if (isHeader) {
    return (
      <SimInventoryTableHeaderCell segmentName={segmentName} columnValue={columnValue}>
        {value as string}
      </SimInventoryTableHeaderCell>
    );
  }

  if (ColumnRenderer) {
    return (
      <ColumnRenderer
        value={sim}
        columnValue={columnValue}
        customProps={{ ...customRendererProps, isHeader }}
      />
    );
  }

  if (typeof value === 'string') {
    if (value.length === 0) {
      return <EmptyState />;
    }
    return value;
  }

  if (isArray(value)) {
    if (process.env.VITE_ENV_NAME === 'development') {
      // eslint-disable-next-line no-console
      console.error({
        error: 'An array value should be rendered with a custom cell renderer.',
        value,
      });
    }
    return <EmptyState />;
  }

  return value ?? <EmptyState />;
};

interface SimInventoryTableCellProps {
  sim?: SimsDaoSim;
  ColumnRenderer?: CustomCellRenderer<SimsDaoSim, unknown>;
  segmentName: SimInventorySegmentName;
  isHeader: boolean;
  customRendererProps: SimCellRendererProps;
  columnValue: SimKey | 'checkbox';
  measureElement: MeasureElement;
  allowToggle: boolean;
  onCheckboxToggled: OnCheckboxToggled;
  onAllCheckboxToggled: OnAllCheckboxToggled;
  rightAligned?: boolean;
}

const SimInventoryTableCellInternal: FC<SimInventoryTableCellProps> = ({
  sim,
  segmentName,
  measureElement,
  ColumnRenderer,
  customRendererProps,
  columnValue,
  isHeader,
  allowToggle,
  onCheckboxToggled,
  onAllCheckboxToggled,
  rightAligned,
}) => {
  const isLoading = !sim;
  const isCheckbox = columnValue === 'checkbox';

  return (
    <div
      ref={
        // Don't measure the loading state
        !isLoading
          ? (el: HTMLDivElement | null) => {
              if (el) {
                measureElement(el, columnValue);
              }
            }
          : undefined
      }
      className={clsx(
        isHeader && !isCheckbox ? styles.headerCellContent : styles.cellContent,
        isLoading && styles.cellLoading,
        isCheckbox && styles.checkboxCellContent
      )}
      title={`${
        columnValue &&
        columnValue !== 'checkbox' &&
        !ColumnRenderer &&
        !isLoading &&
        sim[columnValue]
          ? sim[columnValue]
          : ''
      }`}
    >
      {isLoading ? (
        columnValue !== 'checkbox' && <LoaderCell rightAligned={rightAligned} />
      ) : (
        <CellContentRenderer
          sim={sim!}
          segmentName={segmentName}
          ColumnRenderer={ColumnRenderer}
          customRendererProps={customRendererProps}
          isHeader={isHeader}
          columnValue={columnValue}
          allowToggle={allowToggle}
          onCheckboxToggled={onCheckboxToggled}
          onAllCheckboxToggled={onAllCheckboxToggled}
        />
      )}
    </div>
  );
};

const SimInventoryTableCell = memo(
  SimInventoryTableCellInternal,
  (prevProps, nextProps) =>
    prevProps.segmentName === nextProps.segmentName &&
    prevProps.allowToggle === nextProps.allowToggle &&
    prevProps.onCheckboxToggled === nextProps.onCheckboxToggled &&
    prevProps.onAllCheckboxToggled === nextProps.onAllCheckboxToggled &&
    prevProps.customRendererProps === nextProps.customRendererProps &&
    prevProps.columnValue === nextProps.columnValue &&
    (nextProps.ColumnRenderer || nextProps.columnValue === 'checkbox'
      ? isEqual(prevProps.sim, nextProps.sim)
      : (prevProps.sim && prevProps.sim[prevProps.columnValue as SimKey]) ===
        (nextProps.sim && nextProps.sim[nextProps.columnValue as SimKey]))
);

interface SimInventoryTableRowProps {
  sim?: SimsDaoSim;
  segmentName: SimInventorySegmentName;
  virtualRow: Partial<VirtualItem>;
  columnVirtualizer: Virtualizer<any, Element>;
  columnItems: Array<VirtualItem>;
  columnDefs: ColumnDefs<SimKey, SimsDaoSim>;
  getColumnWidth: (value: SimKey) => number;
  measureElement: MeasureElement;
  isHeader?: boolean;
  customRendererProps: SimCellRendererProps;
  allowToggle: boolean;
  onCheckboxToggled: OnCheckboxToggled;
  onAllCheckboxToggled: OnAllCheckboxToggled;
}

const SimInventoryTableRow = forwardRef<HTMLDivElement, SimInventoryTableRowProps>(
  (
    {
      sim,
      segmentName,
      virtualRow,
      columnVirtualizer,
      columnItems,
      columnDefs,
      getColumnWidth,
      measureElement,
      isHeader = false,
      customRendererProps,
      allowToggle,
      onCheckboxToggled,
      onAllCheckboxToggled,
    },
    ref
  ) => {
    const rowStyle: CSSProperties = {
      height: `${virtualRow.size}px`,
      minWidth: `${columnVirtualizer.getTotalSize()}px`,
    };

    if (!isHeader) {
      rowStyle.transform = `translateY(${virtualRow.start}px)`;
    }

    return (
      <div
        style={rowStyle}
        data-index={virtualRow.index}
        className={clsx(!isHeader && styles.row, isHeader && styles.headerRow)}
        ref={ref}
      >
        {columnItems.map((virtualColumn) => {
          const {
            value: columnValue,
            renderer: ColumnRenderer,
            rightAligned,
            numeric,
          } = [...columnDefs.values()][virtualColumn.index];
          return (
            <div
              key={virtualColumn.key}
              data-index={virtualColumn.index}
              ref={columnVirtualizer.measureElement}
              style={{
                width: `${getColumnWidth(columnValue)}px`,
                transform: `translateX(${virtualColumn.start}px)`,
              }}
              className={clsx(
                isHeader ? styles.headerOuterCell : styles.outerCell,
                rightAligned && styles.rightAligned,
                numeric && styles.numeric,
                virtualColumn.index === 0 && styles.checkboxOuterCell
              )}
            >
              <SimInventoryTableCell
                sim={sim}
                segmentName={segmentName}
                measureElement={measureElement}
                ColumnRenderer={ColumnRenderer}
                customRendererProps={customRendererProps}
                isHeader={isHeader}
                columnValue={columnValue}
                allowToggle={allowToggle}
                onCheckboxToggled={onCheckboxToggled}
                onAllCheckboxToggled={onAllCheckboxToggled}
                rightAligned={rightAligned}
              />
            </div>
          );
        })}
      </div>
    );
  }
);

const SimInventoryTableRowMemoized = memo(
  SimInventoryTableRow,
  (prevProps, nextProps) =>
    (prevProps.sim === undefined && nextProps.sim === undefined) ||
    (prevProps.segmentName === nextProps.segmentName &&
      prevProps.allowToggle === nextProps.allowToggle &&
      prevProps.onCheckboxToggled === nextProps.onCheckboxToggled &&
      prevProps.onAllCheckboxToggled === nextProps.onAllCheckboxToggled &&
      prevProps.customRendererProps === nextProps.customRendererProps &&
      isEqual(prevProps.sim, nextProps.sim) &&
      isEqual(prevProps.columnItems, nextProps.columnItems) && // TODO: Improve performance by only checking for changes to columnItem.start
      isEqual(prevProps.getColumnWidth, nextProps.getColumnWidth))
);

export default SimInventoryTableRowMemoized;
